/**
 * Product title autocomplete for a single text input.
 *
 * Contract: <product-suggest> contains exactly one <input> and one <ul>.
 * Both are found via querySelector; if either is missing, handlers no-op.
 *
 * Styles: load product-suggest.css alongside product-suggest.js.
 */
const MIN_LENGTH = 3;
const LIMIT = 10;
const DEBOUNCE_MS = 250;

class ProductSuggest extends HTMLElement {
  #listeners = new AbortController();
  /** @type {AbortController | null} */
  #request = null;
  #timer = 0;

  connectedCallback() {
    const { signal } = this.#listeners;

    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener('pointerdown', this.#handleOutsidePointer, { signal });
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
  }

  get #input() {
    return this.querySelector('input');
  }

  get #list() {
    return this.querySelector('ul');
  }

  get #options() {
    const list = this.#list;
    if (!list) return [];
    return Array.from(list.querySelectorAll('[role="option"]'));
  }

  get #activeIndex() {
    return this.#options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
  }

  #handleInput = () => {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#search(), DEBOUNCE_MS);
  };

  #handleKeyDown = (event) => {
    const list = this.#list;
    const input = this.#input;
    if (!list || !input || list.hidden) return;

    const options = this.#options;
    if (!options.length) return;

    const activeIndex = this.#activeIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActiveIndex(activeIndex < options.length - 1 ? activeIndex + 1 : 0);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#setActiveIndex(activeIndex > 0 ? activeIndex - 1 : options.length - 1);
        break;

      case 'Enter':
        if (activeIndex >= 0) {
          event.preventDefault();
          this.#selectOption(options[activeIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#close();
        break;
    }
  };

  #handleClick = (event) => {
    const item = event.target instanceof Element ? event.target.closest('li') : null;
    if (!item || !this.#list?.contains(item)) return;

    this.#selectOption(item);
  };

  #handleFocusOut = (event) => {
    // relatedTarget is empty when tapping a non-focusable area on mobile; in that
    // case #handleOutsidePointer handles close so the suggestion click can fire first.
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.#close();
    }
  };

  #handleOutsidePointer = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) {
      this.#close();
    }
  };

  async #search() {
    const input = this.#input;
    const list = this.#list;
    if (!input || !list) return;

    const query = input.value.trim();
    if (query.length < MIN_LENGTH) {
      this.#close();
      return;
    }

    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;

    try {
      const url = new URL(`${Theme.routes.predictive_search_url}.json`, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(LIMIT));
      url.searchParams.set('resources[options][unavailable_products]', 'show');

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return;

      const data = await response.json();
      if (controller.signal.aborted) return;

      const products = data?.resources?.results?.products;
      if (!Array.isArray(products) || !products.length) {
        this.#close();
        return;
      }

      const titles = [...new Set(products.map((product) => product.title).filter(Boolean))];
      if (!titles.length) {
        this.#close();
        return;
      }

      this.#render(titles);
    } catch {
      // Aborted or network error — hide suggestions silently.
    }
  }

  /** @param {string[]} titles */
  #render(titles) {
    const input = this.#input;
    const list = this.#list;
    if (!input || !list) return;

    const items = titles.map((title, index) => {
      const item = document.createElement('li');
      item.setAttribute('role', 'option');
      item.id = `${list.id}-${index}`;
      item.textContent = title;
      return item;
    });

    list.replaceChildren(...items);
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    input.removeAttribute('aria-activedescendant');
  }

  /** @param {HTMLElement} option */
  #selectOption(option) {
    const input = this.#input;
    if (!input) return;

    input.value = option.textContent;
    this.#close();
    input.focus();
  }

  #setActiveIndex(index) {
    const input = this.#input;
    const options = this.#options;
    if (!input || !options.length) return;

    for (const [optionIndex, option] of options.entries()) {
      if (optionIndex === index) {
        option.setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else {
        option.removeAttribute('aria-selected');
      }
    }
  }

  #close() {
    const input = this.#input;
    const list = this.#list;
    if (!list) return;

    list.hidden = true;
    list.replaceChildren();
    input?.setAttribute('aria-expanded', 'false');
    input?.removeAttribute('aria-activedescendant');
  }
}

if (!customElements.get('product-suggest')) {
  customElements.define('product-suggest', ProductSuggest);
}
